import { useForm } from "@mantine/form";
import {
  Button,
  styled,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { generateSecret } from "../utils";
import { WebhookEventSelector } from "./WebhookEventSelector";
import React from "react";
import shortUUID from "short-uuid";

const FormContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 8px;

  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  min-height: 50px;

  background-color: ${(props) => props.theme.palette.background.default};
`;

export type WebhookFormData = {
  name: string;
  description: string;
  url: string;
  uuid: string;
  events: string[];
  secret: string;
};

export const DefaultWebhookForm: React.FC<{
  defaultFormData?: WebhookFormData;
  onSubmit?: (data: WebhookFormData) => void;
  submitting?: boolean;
}> = ({ defaultFormData, onSubmit, submitting }) => {
  const form = useForm({
    initialValues: {
      name: defaultFormData?.name || "",
      description: defaultFormData?.description || "",
      url: defaultFormData?.url || "",
      events: defaultFormData?.events || [],
      secret: defaultFormData?.secret || generateSecret(),
      uuid: defaultFormData?.uuid || shortUUID.uuid(),
    },
    validate: (values) => {
      const errors: any = {};

      if (!values.name) {
        errors.name = "This field is required";
      }

      if (!values.url) {
        errors.url = "This field is required";
      }

      if (!values.events || values.events.length === 0) {
        errors.events = "At least one event must be selected";
      }

      if (values.url && !/^https?:\/\/.+/.test(values.url)) {
        errors.url = "Invalid URL format";
      }

      if (!values.secret) {
        errors.secret = "This field is required";
      }

      return errors;
    },
  });

  const handleSubmit = () => {
    const validationResults = form.validate();

    if (validationResults.hasErrors) {
      return;
    }

    const formData: WebhookFormData = form.getValues();

    if (onSubmit) {
      onSubmit(formData);
    }

    form.resetDirty();
  };

  return (
    <FormContainer>
      <TextInput
        label="Webhook Name"
        placeholder="Enter webhook name"
        description="This name will be used to identify the webhook."
        required={true}
        key={form.key("name")}
        {...form.getInputProps("name")}
      />
      <TextInput
        label="Webhook URL"
        placeholder="Enter webhook URL"
        description="This URL will be used to send the webhook events."
        required={true}
        key={form.key("url")}
        {...form.getInputProps("url")}
      />
      <TextAreaInput
        label="Webhook Description"
        placeholder="Enter webhook description"
        description="This description will be used to describe the webhook."
        key={form.key("description")}
        {...form.getInputProps("description")}
      />
      <TextInput
        label="Signing Secret"
        description="This secret will be used to authenticate the webhook."
        readOnly={true}
        key={form.key("secret")}
        {...form.getInputProps("secret")}
      />
      <div>
        <WebhookEventSelector
          key={form.key("events")}
          {...form.getInputProps("events")}
        />
      </div>
      <Button
        size="xs"
        variant="contained"
        color="primary"
        onClick={() => handleSubmit()}
        style={{ marginLeft: "auto" }}
        disabled={!form.isDirty()}
        loading={submitting}
      >
        {defaultFormData ? "Save Webhook" : "Create Webhook"}
      </Button>
    </FormContainer>
  );
};
